import { ConfigurationType, SidebarSectionType } from 'data/types';
import { getConfigurationTabForStep } from 'utils/side-panel-tabs';

export type ConfigurationTabId =
  | 'condensers'
  | 'zones'
  | 'air_handlers'
  | 'line_sets';

export type ConfigurationTabStatus = {
  summary?: string;
  complete: boolean;
};

const airHandlersSummary = (
  zonesWithItemsCount: number,
  zonesWithAirHandlersCount: number
): string | undefined =>
  zonesWithAirHandlersCount > 0
    ? `${zonesWithAirHandlersCount} of ${zonesWithItemsCount}`
    : undefined;

const isTabComplete = (
  sections: SidebarSectionType[],
  tabId: ConfigurationTabId
): boolean => {
  const tabSections = sections.filter(
    (section) =>
      section.requirementsMet !== undefined &&
      getConfigurationTabForStep(section.id) === tabId
  );
  return (
    tabSections.length > 0 &&
    tabSections.every((section) => section.requirementsMet === true)
  );
};

export function getConfigurationTabStatuses(
  configuration: ConfigurationType | undefined,
  sections: SidebarSectionType[]
): Record<ConfigurationTabId, ConfigurationTabStatus> {
  const placedCondenserCount =
    configuration?.condensers.filter((c) => c.position !== undefined).length ??
    0;

  const zonesWithItems =
    configuration?.condensers
      .flatMap((condenser) => condenser.zones)
      .filter((zone) => zone.zoneItems.length > 0) ?? [];

  const zonesWithAirHandlers = zonesWithItems.filter(
    (zone) => zone.airHandler !== undefined
  );

  const linkedAirHandlerCount = zonesWithAirHandlers.filter((zone) =>
    configuration?.lineSets?.some(
      (lineSet) => lineSet.airHandlerUniqueId === zone.airHandler?.uniqueId
    )
  ).length;

  return {
    condensers: {
      summary:
        placedCondenserCount > 0 ? `${placedCondenserCount} placed` : undefined,
      complete: isTabComplete(sections, 'condensers')
    },
    zones: {
      summary:
        zonesWithItems.length > 0
          ? `${zonesWithItems.length} ${
              zonesWithItems.length === 1 ? 'zone' : 'zones'
            }`
          : undefined,
      complete: isTabComplete(sections, 'zones')
    },
    air_handlers: {
      summary: airHandlersSummary(
        zonesWithItems.length,
        zonesWithAirHandlers.length
      ),
      complete: isTabComplete(sections, 'air_handlers')
    },
    line_sets: {
      summary:
        linkedAirHandlerCount > 0
          ? `${linkedAirHandlerCount} of ${zonesWithAirHandlers.length}`
          : undefined,
      complete: isTabComplete(sections, 'line_sets')
    }
  };
}
